import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Download, ExternalLink, Eye, EyeOff, Pencil, X, Upload, Search, Megaphone, Handshake } from "lucide-react";
import DiscordSecurity from "@/components/DiscordSecurity";
import SiteSettings from "@/components/SiteSettings";
import ReportCards from "@/components/ReportCards";
import LiveStandings from "@/components/LiveStandings";
import VerificationAdmin from "@/components/VerificationAdmin";
import MembershipsAdmin from "@/components/MembershipsAdmin";
import MerchAdmin from "@/components/MerchAdmin";
import ModerationPanel from "@/components/ModerationPanel";
import BridgeAudit from "@/components/BridgeAudit";
import DefconAdmin from "@/components/DefconAdmin";
import DonationCharactersAdmin from "@/components/DonationCharactersAdmin";
import PolicyAdmin from "@/components/policies/PolicyAdmin";
import ContractsAdmin from "@/components/contracts/ContractsAdmin";
import PayoutsAdmin from "@/components/PayoutsAdmin";
import DiscordUserProfile from "@/components/discord/DiscordUserProfile";
import { mergeGuilds } from "@/components/discord/UserServers";
import { CollabBadge, CollabCheckbox } from "@/components/CollabBadge";
import MeetingsPanel from "@/components/meetings/MeetingsPanel";
import MeetingAlert from "@/components/meetings/MeetingAlert";
import { useMeetings } from "@/components/meetings/useMeetings";
import NoFeeToggle from "@/components/NoFeeToggle";
import { splitLabel } from "@/lib/splits";

// Full site-admin panel — Founder-only.
const TABS_FOUNDER = ["Overview", "Employees", "Discord Users", "Departments & Brands", "Shows", "Hosts", "HR Cases", "Applications", "About Page", "Policies", "Contracts", "Meetings", "Memberships", "Payouts", "Merch", "Donation Alerts", "Report Cards", "Live Standings", "Verification", "Moderation", "Discord Security", "Defcon", "Bridge", "Site Settings"];
// Discord-security-only tier — Admin role (Discord operations, no site admin panel).
const TABS_ADMIN = ["Overview", "Policies", "Meetings", "Moderation", "Discord Security", "Defcon", "Bridge", "Site Settings"];
// Moderator role — moderation tools only.
const TABS_MOD = ["Overview", "Moderation"];
const TABS_EMP = ["Overview", "HR Cases"];

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const STATUSES = ["Open", "Under Review", "Closed"];

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "Meetings" ? "Meetings" : "Overview"));
  const meetings = useMeetings(!!user && !user.is_banned && (["founder", "executive", "admin"].includes(user.role) || !!user.roles?.includes("host")));

  if (loading) return <div className="p-12 text-[var(--ff-text-dim)]">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_banned) return <Navigate to="/banned" replace />;

  const role = user.role;
  const isFounder = role === "founder";
  const isExecutive = role === "executive";
  const isAdminTier = role === "admin";
  const isModerator = role === "moderator";
  const isEmployee = isFounder || isExecutive || isAdminTier || isModerator || (user.roles && user.roles.includes("employee"));
  if (!isEmployee) {
    if (user.roles && user.roles.includes("host")) return <Navigate to="/host-studio" replace />;
    return <Navigate to="/watchlist" replace />;
  }

  // Founder + Executive Manager get the full site admin panel.
  const canFounder = isFounder || isExecutive;
  const canAdmin = isFounder || isExecutive || isAdminTier;    // Discord Security + Moderation
  const canMod = isFounder || isExecutive || isAdminTier || isModerator;
  const tabs = canFounder ? TABS_FOUNDER : (canAdmin ? TABS_ADMIN : (canMod ? TABS_MOD : TABS_EMP));
  const roleLabel = isFounder ? "FOUNDER" : isExecutive ? "EXECUTIVE MANAGER" : isAdminTier ? "ADMIN" : isModerator ? "MODERATOR" : "EMPLOYEE";

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
        <div>
          <div className="label-eyebrow text-[var(--ff-red)] mb-2">▮ {roleLabel} / CONTROL ROOM</div>
          <h1 className="font-display text-4xl lg:text-5xl">Welcome, {user.name}.</h1>
          <p className="text-[var(--ff-text-dim)] text-sm mt-2 font-mono-ui">
            EMP-ID: {user.employee_id || "—"} / {user.department || "—"} / {user.position || "—"}
          </p>
        </div>
      </div>

      {(isExecutive || user.roles?.includes("host")) && <MeetingAlert {...meetings} onOpen={() => setTab("Meetings")} />}

      <div className="flex flex-wrap gap-px bg-[var(--ff-border)] mb-8" data-testid="dashboard-tabs">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`tab-${t.replace(/[ &]/g, "-").toLowerCase()}`}
            className={`px-5 py-3 font-mono-ui text-xs uppercase tracking-[0.18em] transition-colors ${
              tab === t ? "bg-[var(--ff-red)] text-white" : "bg-[var(--ff-surface)] text-[var(--ff-text-dim)] hover:text-white"
            }`}
          >
            {t}
            {t === "Meetings" && meetings.data?.pending_count > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-white text-black text-[0.6rem] font-bold" data-testid="tab-meetings-badge">{meetings.data.pending_count}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "Overview" && <Overview user={user} />}
      {tab === "Employees" && canFounder && <Employees />}
      {tab === "Discord Users" && canFounder && <DiscordUsers />}
      {tab === "Departments & Brands" && canFounder && <DeptBrands />}
      {tab === "Shows" && canFounder && <Shows />}
      {tab === "Hosts" && canFounder && <HostsAdmin />}
      {tab === "HR Cases" && <HRCases isAdmin={canFounder} />}
      {tab === "Applications" && canFounder && <Applications />}
      {tab === "About Page" && canFounder && <AboutEditor />}
      {tab === "Discord Security" && canAdmin && <DiscordSecurity />}
      {tab === "Site Settings" && canAdmin && <SiteSettings />}
      {tab === "Report Cards" && canFounder && <ReportCards />}
      {tab === "Live Standings" && canFounder && <LiveStandings />}
      {tab === "Verification" && canFounder && <VerificationAdmin />}
      {tab === "Memberships" && canFounder && <MembershipsAdmin />}
      {tab === "Merch" && canFounder && <MerchAdmin />}
      {tab === "Policies" && canAdmin && <PolicyAdmin />}
      {tab === "Contracts" && canFounder && <ContractsAdmin />}
      {tab === "Meetings" && canAdmin && <MeetingsPanel {...meetings} />}
      {tab === "Payouts" && canFounder && <PayoutsAdmin />}
      {tab === "Donation Alerts" && canFounder && <DonationCharactersAdmin />}
      {tab === "Moderation" && canMod && <ModerationPanel />}
      {tab === "Defcon" && canAdmin && <DefconAdmin />}
      {tab === "Bridge" && canAdmin && <BridgeAudit />}
    </div>
  );
}

function Overview({ user }) {
  const [stats, setStats] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const [d, b, s, h] = await Promise.all([
          api.get("/departments"), api.get("/brands"), api.get("/shows"), api.get("/hr-cases"),
        ]);
        setStats({ depts: d.data.length, brands: b.data.length, shows: s.data.length, cases: h.data.length });
      } catch {}
    })();
  }, []);
  return (
    <div className="grid md:grid-cols-4 gap-px bg-[var(--ff-border)]" data-testid="overview-stats">
      {[
        ["DEPARTMENTS", stats.depts ?? "—"],
        ["BRANDS", stats.brands ?? "—"],
        ["SHOWS", stats.shows ?? "—"],
        ["HR CASES", stats.cases ?? "—"],
      ].map(([l, v]) => (
        <div key={l} className="bg-[var(--ff-surface)] p-8">
          <div className="label-eyebrow mb-3">{l}</div>
          <div className="font-display text-5xl">{v}</div>
        </div>
      ))}
    </div>
  );
}

function Employees() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "", employee_id: "", department: "", position: "" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", employee_id: "", department: "", position: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const load = async () => { const { data } = await api.get("/admin/employees"); setList(data); };
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/employees", form);
      toast.success("Employee created");
      setForm({ email: "", password: "", name: "", employee_id: "", department: "", position: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    await api.delete(`/admin/employees/${id}`); load();
  };
  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      name: u.name || "", email: u.email || "", employee_id: u.employee_id || "",
      department: u.department || "", position: u.position || "",
    });
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await api.put(`/admin/employees/${editing.id}`, editForm);
      toast.success("Employee updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
    finally { setSavingEdit(false); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-8">
      <form onSubmit={create} className="surface-card p-6 space-y-3" data-testid="create-employee-form">
        <h3 className="font-display text-2xl mb-2">Create Employee</h3>
        {[
          ["name", "Name"], ["email", "Email"], ["password", "Password"],
          ["employee_id", "Employee ID"], ["department", "Department"], ["position", "Position"],
        ].map(([k, l]) => (
          <div key={k}>
            <label className="label-eyebrow block mb-1">{l}</label>
            <input required={["name", "email", "password", "employee_id"].includes(k)}
                   type={k === "password" ? "password" : "text"}
                   value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                   className="w-full px-3 py-2 text-sm" data-testid={`emp-${k}-input`} />
          </div>
        ))}
        <button className="btn-primary w-full justify-center" data-testid="emp-create-btn"><Plus size={14}/>Create</button>
      </form>
      <div className="lg:col-span-2 surface-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--ff-surface-hover)]">
            <tr><th className="text-left p-3 label-eyebrow">Name</th><th className="text-left p-3 label-eyebrow">Email</th><th className="text-left p-3 label-eyebrow">Role</th><th className="text-left p-3 label-eyebrow">Emp ID</th><th></th></tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} className="border-t border-[var(--ff-border)]" data-testid={`emp-row-${u.id}`}>
                <td className="p-3">
                  <div>{u.name}</div>
                  {u.discord_id && (
                    <span className="inline-block mt-1 text-[0.55rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] border border-[var(--ff-border)] rounded px-1.5 py-0.5" data-testid={`emp-discord-synced-${u.id}`} title="Name may re-sync from Discord on the next member sync">
                      Discord-synced
                    </span>
                  )}
                </td>
                <td className="p-3 font-mono-ui text-xs">{u.email || "—"}</td>
                <td className="p-3"><span className="font-mono-ui text-xs uppercase tracking-[0.15em]" style={{ color: u.role === "admin" ? "var(--ff-red)" : "var(--ff-text-dim)" }}>{u.role}</span></td>
                <td className="p-3 font-mono-ui text-xs">{u.employee_id || "—"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(u)} className="text-[var(--ff-text-dim)] hover:text-white mr-3" data-testid={`edit-emp-${u.id}`} title="Edit"><Pencil size={14}/></button>
                  {u.role !== "admin" && u.role !== "founder" && <button onClick={() => del(u.id)} className="text-[var(--ff-red)]" data-testid={`del-emp-${u.id}`} title="Delete"><Trash2 size={14}/></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="edit-employee-modal" onClick={() => !savingEdit && setEditing(null)}>
          <form onSubmit={saveEdit} className="surface-card p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-2xl">Edit Employee</h3>
              <button type="button" onClick={() => setEditing(null)} className="text-[var(--ff-text-dim)] hover:text-white" data-testid="edit-emp-cancel-btn"><X size={18}/></button>
            </div>
            {editing.discord_id && (
              <div className="text-[0.65rem] font-mono-ui text-[var(--ff-text-dim)] border border-[var(--ff-border)] rounded px-2 py-1.5" data-testid="edit-emp-discord-note">
                This is a Discord-linked account — the Name may be overwritten on the next Discord member sync.
              </div>
            )}
            {[
              ["name", "Name"], ["email", "Email"], ["employee_id", "Employee ID"],
              ["department", "Department"], ["position", "Position"],
            ].map(([k, l]) => (
              <div key={k}>
                <label className="label-eyebrow block mb-1">{l}</label>
                <input value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })}
                       className="w-full px-3 py-2 text-sm" data-testid={`edit-emp-${k}-input`} />
              </div>
            ))}
            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={() => setEditing(null)} className="btn-ghost text-xs">Cancel</button>
              <button type="submit" disabled={savingEdit} className="btn-primary text-xs" data-testid="edit-emp-save-btn">
                {savingEdit ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function DiscordUsers() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | verified | banned
  const [granting, setGranting] = useState(null); // "userId:role"
  const [openId, setOpenId] = useState(null);
  const [ctx, setCtx] = useState({ blacklistIds: new Set(), homeGuildId: "", defaultGrace: 24 });
  const loadCtx = async () => {
    try {
      const [bl, ss] = await Promise.all([api.get("/admin/blacklist"), api.get("/admin/site-settings")]);
      setCtx({ blacklistIds: new Set(bl.data.map((b) => b.guild_id)), homeGuildId: ss.data.home_guild_id || "", defaultGrace: ss.data.blacklist_default_grace_hours ?? 24 });
    } catch { /* profile still renders without blacklist markers */ }
  };
  const load = async () => {
    try {
      const { data } = await api.get("/admin/discord-users");
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { load(); loadCtx(); }, []);

  const grant = async (userId, role, username) => {
    if (!window.confirm(`Grant the ${role.toUpperCase()} Discord role to ${username || "this user"}?\n\nThey'll get the role on Discord immediately and move to the Employees tab.`)) return;
    setGranting(`${userId}:${role}`);
    try {
      const { data } = await api.post(`/admin/users/${userId}/assign-staff-role`, { role });
      toast.success(`Granted ${role} role · new roles: ${(data.roles || []).join(", ")}`);
      setList((prev) => (prev || []).filter((u) => u.id !== userId));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Grant failed");
    } finally { setGranting(null); }
  };

  const filtered = React.useMemo(() => {
    if (!list) return [];
    const needle = q.trim().toLowerCase();
    return list.filter((u) => {
      if (filter === "verified" && !u.is_verified) return false;
      if (filter === "banned" && !u.is_banned) return false;
      if (!needle) return true;
      return (
        (u.discord_username || "").toLowerCase().includes(needle) ||
        (u.discord_id || "").includes(needle) ||
        (u.email || "").toLowerCase().includes(needle)
      );
    });
  }, [list, q, filter]);

  return (
    <div data-testid="discord-users-tab">
      <div className="flex items-center gap-3 mb-6">
        <div>
          <div className="label-eyebrow text-[var(--ff-red)]">▮ DISCORD USERS</div>
          <h2 className="font-display text-3xl">Fans Signed In With Discord</h2>
          <p className="text-[var(--ff-text-dim)] text-sm mt-1 font-mono-ui">
            Every regular fan who signed in with Discord — no employee, host, moderator, admin, or founder role.
            Staff accounts live on the Employees tab.
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-dim)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, Discord ID or email…"
            className="w-full pl-9 pr-3 py-2.5 text-sm" data-testid="discord-users-search" />
        </div>
        <div className="flex gap-px bg-[var(--ff-border)]">
          {[["all", "All"], ["verified", "Verified"], ["banned", "Banned"]].map(([id, label]) => (
            <button key={id} onClick={() => setFilter(id)}
              className={`px-3 py-2.5 font-mono-ui text-xs uppercase tracking-[0.15em] ${filter === id ? "bg-[var(--ff-red)] text-white" : "bg-[var(--ff-surface)] text-[var(--ff-text-dim)] hover:text-white"}`}
              data-testid={`discord-users-filter-${id}`}>{label}</button>
          ))}
        </div>
        <div className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]" data-testid="discord-users-count">
          {list ? `${filtered.length} of ${list.length}` : "…"}
        </div>
      </div>

      {list === null && <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading Discord users…</div>}
      {list && (
        <div className="surface-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--ff-surface-hover)]">
              <tr>
                <th className="text-left p-3 label-eyebrow">User</th>
                <th className="text-left p-3 label-eyebrow">Discord ID</th>
                <th className="text-left p-3 label-eyebrow">Verified</th>
                <th className="text-left p-3 label-eyebrow">Status</th>
                <th className="text-left p-3 label-eyebrow">Joined</th>
                <th className="text-right p-3 label-eyebrow">Promote</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <React.Fragment key={u.id}>
                <tr className="border-t border-[var(--ff-border)]" data-testid={`discord-user-row-${u.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {u.discord_avatar
                        ? <img src={u.discord_avatar} alt="" className="w-7 h-7 rounded-full shrink-0" />
                        : <div className="w-7 h-7 rounded-full bg-[var(--ff-red)] text-white flex items-center justify-center text-[0.6rem]">?</div>}
                      <div className="min-w-0">
                        <div className="truncate">{u.discord_username || u.name || "—"}</div>
                        {u.is_founding_member && (
                          <div className="text-[0.55rem] font-mono-ui uppercase tracking-[0.15em] text-amber-300">
                            Founder #{u.founding_member_number}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 font-mono-ui text-xs text-[var(--ff-text-dim)]">{u.discord_id}</td>
                  <td className="p-3">
                    {u.is_verified
                      ? <span className="px-2 py-0.5 border border-green-500/50 text-green-300 text-[0.55rem] uppercase tracking-[0.15em] font-mono-ui">Yes</span>
                      : <span className="px-2 py-0.5 border border-yellow-500/40 text-yellow-300 text-[0.55rem] uppercase tracking-[0.15em] font-mono-ui">No</span>}
                  </td>
                  <td className="p-3">
                    {u.is_banned
                      ? <span className="px-2 py-0.5 border border-red-500/50 text-red-300 text-[0.55rem] uppercase tracking-[0.15em] font-mono-ui">Banned</span>
                      : <span className="px-2 py-0.5 border border-green-500/50 text-green-300 text-[0.55rem] uppercase tracking-[0.15em] font-mono-ui">Active</span>}
                  </td>
                  <td className="p-3 font-mono-ui text-xs text-[var(--ff-text-dim)]">{(u.created_at || "").slice(0, 10)}</td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={() => setOpenId(openId === u.id ? null : u.id)}
                        className={`btn-ghost text-[0.6rem] whitespace-nowrap ${openId === u.id ? "text-white border-white/40" : ""}`}
                        data-testid={`discord-user-profile-btn-${u.id}`}
                      >
                        {openId === u.id ? "Close" : (() => { const n = mergeGuilds(u.discord_guilds, u.discord_guilds_full).length; return `Profile · ${n} server${n === 1 ? "" : "s"}`; })()}
                      </button>
                      <button
                        onClick={() => grant(u.id, "host", u.discord_username)}
                        disabled={!!granting || u.is_banned}
                        title={u.is_banned ? "Unban first before promoting" : "Grant this user the Discord Host role"}
                        className="btn-ghost text-[0.6rem] whitespace-nowrap disabled:opacity-40"
                        data-testid={`discord-user-grant-host-${u.id}`}
                      >
                        {granting === `${u.id}:host` ? "…" : "Grant Host"}
                      </button>
                      <button
                        onClick={() => grant(u.id, "employee", u.discord_username)}
                        disabled={!!granting || u.is_banned}
                        title={u.is_banned ? "Unban first before promoting" : "Grant this user the Discord Employee role"}
                        className="btn-ghost text-[0.6rem] whitespace-nowrap disabled:opacity-40"
                        data-testid={`discord-user-grant-employee-${u.id}`}
                      >
                        {granting === `${u.id}:employee` ? "…" : "Grant Employee"}
                      </button>
                      <button
                        onClick={() => grant(u.id, "executive", u.discord_username)}
                        disabled={!!granting || u.is_banned}
                        title={u.is_banned ? "Unban first before promoting" : "Grant Executive Manager (full founder-level power)"}
                        className="btn-ghost text-[0.6rem] whitespace-nowrap disabled:opacity-40 text-[var(--ff-red)] border-[var(--ff-red)]/40"
                        data-testid={`discord-user-grant-executive-${u.id}`}
                      >
                        {granting === `${u.id}:executive` ? "…" : "Grant Executive"}
                      </button>
                    </div>
                  </td>
                </tr>
                {openId === u.id && (
                  <tr><td colSpan={6} className="p-0"><DiscordUserProfile u={u} ctx={ctx} onBlacklisted={() => { loadCtx(); load(); }} /></td></tr>
                )}
                </React.Fragment>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-[var(--ff-text-dim)] font-mono-ui text-sm">No Discord users match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DeptBrands() {
  const [depts, setDepts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [dForm, setDForm] = useState({ name: "", description: "" });
  const [bForm, setBForm] = useState({ name: "", department_id: "", description: "", website: "", logo_url: "" });
  const [editingDept, setEditingDept] = useState(null);
  const [editingBrand, setEditingBrand] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const load = async () => {
    const [d, b] = await Promise.all([api.get("/departments"), api.get("/brands")]);
    setDepts(d.data); setBrands(b.data);
  };
  useEffect(() => { load(); }, []);
  const resetDept = () => { setDForm({ name: "", description: "" }); setEditingDept(null); };
  const resetBrand = () => { setBForm({ name: "", department_id: "", description: "", website: "", logo_url: "" }); setEditingBrand(null); };
  const submitDept = async (e) => {
    e.preventDefault();
    try {
      if (editingDept) { await api.put(`/departments/${editingDept}`, dForm); toast.success("Department updated"); }
      else { await api.post("/departments", dForm); toast.success("Department added"); }
      resetDept(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submitBrand = async (e) => {
    e.preventDefault();
    if (!bForm.department_id) return toast.error("Choose a department");
    try {
      if (editingBrand) { await api.put(`/brands/${editingBrand}`, bForm); toast.success("Brand updated"); }
      else { await api.post("/brands", bForm); toast.success("Brand added"); }
      resetBrand(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEditDept = (d) => { setEditingDept(d.id); setDForm({ name: d.name, description: d.description || "" }); };
  const startEditBrand = (b) => { setEditingBrand(b.id); setBForm({ name: b.name, department_id: b.department_id, description: b.description || "", website: b.website || "", logo_url: b.logo_url || "" }); };
  const delDept = async (id) => { if (window.confirm("Delete department and its brands?")) { await api.delete(`/departments/${id}`); if (editingDept === id) resetDept(); load(); } };
  const delBrand = async (id) => { await api.delete(`/brands/${id}`); if (editingBrand === id) resetBrand(); load(); };
  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${API_BASE.replace(/\/api$/, "")}${data.url}`;
      setBForm((f) => ({ ...f, logo_url: url }));
      toast.success("Logo uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploadingLogo(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="surface-card p-6">
        <h3 className="font-display text-2xl mb-4">Departments</h3>
        <form onSubmit={submitDept} className="space-y-3 mb-6">
          <input required placeholder="Name" value={dForm.name} onChange={(e) => setDForm({ ...dForm, name: e.target.value })} className="w-full px-3 py-2" data-testid="dept-name-input" />
          <textarea placeholder="Description" value={dForm.description} onChange={(e) => setDForm({ ...dForm, description: e.target.value })} className="w-full px-3 py-2" rows={2} />
          <div className="flex gap-2">
            <button className="btn-primary" data-testid={editingDept ? "update-dept-btn" : "add-dept-btn"}>
              {editingDept ? <Pencil size={14}/> : <Plus size={14}/>}
              {editingDept ? "Update Department" : "Add Department"}
            </button>
            {editingDept && <button type="button" onClick={resetDept} className="btn-ghost" data-testid="cancel-dept-edit-btn"><X size={14}/>Cancel</button>}
          </div>
        </form>
        <div className="space-y-2">
          {depts.map((d) => (
            <div key={d.id} className={`flex justify-between items-center bg-[var(--ff-bg)] p-3 border ${editingDept === d.id ? "border-[var(--ff-red)]" : "border-[var(--ff-border)]"}`} data-testid={`dept-row-${d.id}`}>
              <div className="flex-1 min-w-0"><div className="font-bold">{d.name}</div><div className="text-xs text-[var(--ff-text-dim)] truncate">{d.description}</div></div>
              <div className="flex items-center gap-3 ml-2">
                <button onClick={() => startEditDept(d)} className="text-[var(--ff-text-dim)] hover:text-white" data-testid={`edit-dept-${d.id}`}><Pencil size={14}/></button>
                <button onClick={() => delDept(d.id)} className="text-[var(--ff-red)]"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="surface-card p-6">
        <h3 className="font-display text-2xl mb-4">Brands</h3>
        <form onSubmit={submitBrand} className="space-y-3 mb-6">
          <input required placeholder="Brand name" value={bForm.name} onChange={(e) => setBForm({ ...bForm, name: e.target.value })} className="w-full px-3 py-2" data-testid="brand-name-input" />
          <select required value={bForm.department_id} onChange={(e) => setBForm({ ...bForm, department_id: e.target.value })} className="w-full px-3 py-2" data-testid="brand-dept-select">
            <option value="">— Department —</option>
            {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <textarea placeholder="Description" value={bForm.description} onChange={(e) => setBForm({ ...bForm, description: e.target.value })} className="w-full px-3 py-2" rows={2} />
          <input placeholder="Website URL" value={bForm.website} onChange={(e) => setBForm({ ...bForm, website: e.target.value })} className="w-full px-3 py-2" />
          <div>
            <label className="label-eyebrow block mb-2">Logo</label>
            <div className="flex items-start gap-3">
              {bForm.logo_url ? (
                <img src={bForm.logo_url} alt="logo" className="w-16 h-16 object-contain bg-white border border-[var(--ff-border)]" data-testid="brand-logo-preview" />
              ) : (
                <div className="w-16 h-16 bg-[var(--ff-bg)] border border-dashed border-[var(--ff-border)] flex items-center justify-center text-[var(--ff-text-dim)] text-xs">
                  LOGO
                </div>
              )}
              <div className="flex-1 space-y-2">
                <label className="btn-ghost text-xs inline-flex cursor-pointer w-full justify-center">
                  <Upload size={12} /> {uploadingLogo ? "Uploading..." : "Upload Logo"}
                  <input type="file" accept="image/*" className="hidden" disabled={uploadingLogo} onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="brand-logo-upload" />
                </label>
                <input placeholder="or paste URL" value={bForm.logo_url} onChange={(e) => setBForm({ ...bForm, logo_url: e.target.value })} className="w-full px-3 py-2 text-xs" data-testid="brand-logo-input" />
                {bForm.logo_url && <button type="button" onClick={() => setBForm({ ...bForm, logo_url: "" })} className="text-xs text-[var(--ff-red)] font-mono-ui uppercase tracking-[0.15em]" data-testid="brand-logo-clear">Remove</button>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-primary" data-testid={editingBrand ? "update-brand-btn" : "add-brand-btn"}>
              {editingBrand ? <Pencil size={14}/> : <Plus size={14}/>}
              {editingBrand ? "Update Brand" : "Add Brand"}
            </button>
            {editingBrand && <button type="button" onClick={resetBrand} className="btn-ghost" data-testid="cancel-brand-edit-btn"><X size={14}/>Cancel</button>}
          </div>
        </form>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {brands.map((b) => (
            <div key={b.id} className={`flex justify-between items-center bg-[var(--ff-bg)] p-3 border ${editingBrand === b.id ? "border-[var(--ff-red)]" : "border-[var(--ff-border)]"}`} data-testid={`brand-row-${b.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {b.logo_url ? (
                  <img src={b.logo_url} alt={b.name} className="w-10 h-10 object-contain bg-white flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 bg-[var(--ff-surface)] border border-[var(--ff-border)] flex items-center justify-center text-[var(--ff-text-dim)] text-[0.6rem] font-mono-ui flex-shrink-0">LOGO</div>
                )}
                <div className="min-w-0">
                  <div className="font-bold truncate">{b.name}</div>
                  <div className="text-xs text-[var(--ff-text-dim)] truncate">{depts.find((d) => d.id === b.department_id)?.name || "—"}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 ml-2">
                <button onClick={() => startEditBrand(b)} className="text-[var(--ff-text-dim)] hover:text-white" data-testid={`edit-brand-${b.id}`}><Pencil size={14}/></button>
                <button onClick={() => delBrand(b.id)} className="text-[var(--ff-red)]"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Shows() {
  const { user } = useAuth();
  const [shows, setShows] = useState([]);
  const emptyForm = { title: "", days_of_week: [], start_time: "", end_time: "", day_times: {}, date_overrides: {}, youtube_url: "", description: "", thumbnail_url: "", month: null, year: null, category: "", hosts: [], showrunner: "", is_recurring: true, is_premiere: false, premiere_date: "", is_finale: false, finale_date: "", season_number: "", season_start: "", season_end: "", is_special: false, special_duration_hours: "", special_frequency: "one_day", is_collab: false };
  const [form, setForm] = useState(emptyForm);
  const [hostsText, setHostsText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [newOvrDate, setNewOvrDate] = useState("");
  const [hostNames, setHostNames] = useState(null);
  const load = async () => { const { data } = await api.get("/shows"); setShows(data); };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/hosts").then(({ data }) => setHostNames(new Set(data.map((h) => h.name)))).catch(() => {}); }, []);
  const reset = () => { setForm(emptyForm); setHostsText(""); setEditingId(null); };
  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, thumbnail_url: `${API_BASE.replace(/\/api$/, "")}${data.url}` }));
      toast.success("Logo uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploadingLogo(false); }
  };
  const toggleDay = (d) => {
    const cur = form.days_of_week || [];
    setForm({ ...form, days_of_week: cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort() });
  };
  const setDayTime = (d, field, value) => {
    setForm((f) => {
      const dt = { ...(f.day_times || {}) };
      dt[String(d)] = { ...(dt[String(d)] || {}), [field]: value };
      return { ...f, day_times: dt };
    });
  };
  const clearDayTime = (d) => {
    setForm((f) => { const dt = { ...(f.day_times || {}) }; delete dt[String(d)]; return { ...f, day_times: dt }; });
  };
  const addDateOverride = () => {
    if (!newOvrDate) return;
    setForm((f) => ({
      ...f,
      date_overrides: { ...(f.date_overrides || {}), [newOvrDate]: (f.date_overrides?.[newOvrDate] || { start: f.start_time || "", end: f.end_time || "" }) },
    }));
    setNewOvrDate("");
  };
  const setDateOverride = (iso, field, value) => {
    setForm((f) => {
      const dov = { ...(f.date_overrides || {}) };
      dov[iso] = { ...(dov[iso] || {}), [field]: value };
      return { ...f, date_overrides: dov };
    });
  };
  const removeDateOverride = (iso) => {
    setForm((f) => { const dov = { ...(f.date_overrides || {}) }; delete dov[iso]; return { ...f, date_overrides: dov }; });
  };
  const submit = async (e) => {
    e.preventDefault();
    const hosts = hostsText.split(",").map((s) => s.trim()).filter(Boolean);
    // If admin chose a Showrunner but then removed that host, clear it.
    const showrunner = hosts.includes((form.showrunner || "").trim()) ? (form.showrunner || "").trim() : (hosts.find((n) => !hostNames || hostNames.has(n)) || hosts[0] || "");
    // Keep only complete per-day / per-date overrides.
    const cleanDayTimes = {};
    Object.entries(form.day_times || {}).forEach(([k, v]) => {
      if (v && v.start && v.end && (form.days_of_week || []).includes(Number(k))) cleanDayTimes[k] = { start: v.start, end: v.end };
    });
    const cleanDateOverrides = {};
    Object.entries(form.date_overrides || {}).forEach(([k, v]) => {
      if (v && v.start && v.end) cleanDateOverrides[k] = { start: v.start, end: v.end };
    });
    const body = {
      ...form,
      day_of_week: (form.days_of_week && form.days_of_week.length > 0) ? form.days_of_week[0] : 0,
      day_times: cleanDayTimes,
      date_overrides: cleanDateOverrides,
      month: form.month ? Number(form.month) : null,
      year: form.year ? Number(form.year) : null,
      season_number: form.season_number ? Number(form.season_number) : null,
      special_duration_hours: form.special_duration_hours ? Number(form.special_duration_hours) : null,
      hosts,
      showrunner,
    };
    try {
      if (editingId) { await api.put(`/shows/${editingId}`, body); toast.success("Show updated"); }
      else { await api.post("/shows", body); toast.success((body.start_time && body.end_time && body.days_of_week?.length > 0) ? "Show added & scheduled" : "Show added (unscheduled)"); }
      reset(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEdit = (s) => {
    setEditingId(s.id);
    const hasDays = Array.isArray(s.days_of_week) && s.days_of_week.length > 0;
    // Unscheduled drafts keep no days; only legacy scheduled shows fall back to day_of_week.
    const days = hasDays ? s.days_of_week : ((s.start_time && s.end_time && s.day_of_week != null) ? [s.day_of_week] : []);
    setForm({
      title: s.title, days_of_week: days, start_time: s.start_time, end_time: s.end_time,
      day_times: s.day_times || {}, date_overrides: s.date_overrides || {},
      youtube_url: s.youtube_url || "", description: s.description || "", thumbnail_url: s.thumbnail_url || "",
      month: s.month, year: s.year, category: s.category || "", hosts: s.hosts || [], showrunner: s.showrunner || "",
      is_recurring: s.is_recurring !== false, is_premiere: !!s.is_premiere, premiere_date: s.premiere_date || "",
      is_finale: !!s.is_finale, finale_date: s.finale_date || "",
      season_number: s.season_number ?? "", season_start: s.season_start || "", season_end: s.season_end || "",
      is_special: !!s.is_special, special_duration_hours: s.special_duration_hours ?? "",
      special_frequency: s.special_frequency || "one_day", is_collab: !!s.is_collab,
    });
    setHostsText((s.hosts || []).join(", "));
  };
  const del = async (id) => { if (window.confirm("Delete show?")) { await api.delete(`/shows/${id}`); if (editingId === id) reset(); load(); } };
  const announce = async (s) => {
    if (!window.confirm(`Post a "New Show" card for "${s.title}" in the Discord announcements channel now?`)) return;
    try { await api.post(`/shows/${s.id}/announce`); toast.success("Announced on Discord"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const DAYS_SHORT = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  return (
    <div className="grid lg:grid-cols-3 gap-8">
      <form onSubmit={submit} className="surface-card p-6 space-y-3" data-testid="add-show-form">
        <h3 className="font-display text-2xl mb-2">{editingId ? "Edit Show" : "Add Show"}</h3>
        <input required placeholder="Show title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2" data-testid="show-title-input" />
        <input list="show-categories" placeholder="Category (Talk, Music, Sports, Podcast, News...)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2" data-testid="show-category-input" />
        <datalist id="show-categories">
          <option value="Talk" /><option value="Music" /><option value="Sports" /><option value="Podcast" /><option value="News" /><option value="Comedy" /><option value="Interview" /><option value="Live Event" />
        </datalist>
        <input placeholder="Hosts (comma-separated)" value={hostsText} onChange={(e) => setHostsText(e.target.value)} className="w-full px-3 py-2" data-testid="show-hosts-input" />
        {(() => {
          const currentHosts = hostsText.split(",").map((s) => s.trim()).filter(Boolean);
          if (currentHosts.length === 0) return null;
          return (
            <div>
              <label className="label-eyebrow block mb-2">
                Showrunner <span className="text-[var(--ff-text-dim)] normal-case tracking-normal">(highest payout share)</span>
              </label>
              <select
                value={form.showrunner || currentHosts.find((n) => !hostNames || hostNames.has(n)) || currentHosts[0]}
                onChange={(e) => setForm({ ...form, showrunner: e.target.value })}
                className="w-full px-3 py-2"
                data-testid="show-showrunner-select"
              >
                {currentHosts.map((h) => {
                  const missing = hostNames && !hostNames.has(h);
                  return <option key={h} value={h} disabled={missing}>★ {h}{missing ? " (no host card)" : ""}</option>;
                })}
              </select>
              <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-1">
                <span data-testid="show-split-hint">{splitLabel(currentHosts.filter((n) => !hostNames || hostNames.has(n)).length, !!shows.find((s) => s.id === editingId)?.no_network_fee)}</span>
              </div>
              {hostNames && currentHosts.some((n) => !hostNames.has(n)) && (
                <div className="text-xs text-yellow-300 mt-1" data-testid="show-unmatched-hosts-warning">
                  No host profile for: {currentHosts.filter((n) => !hostNames.has(n)).join(", ")} — they get no cut until a host card with that exact name exists.
                </div>
              )}
            </div>
          );
        })()}
        {user?.role === "founder" && <NoFeeToggle show={shows.find((s) => s.id === editingId)} onChanged={load} />}
        <div>
          <label className="label-eyebrow block mb-2">Days on air <span className="text-[var(--ff-text-dim)] normal-case tracking-normal">(leave empty to save as unscheduled)</span></label>
          <div className="grid grid-cols-7 gap-1" data-testid="show-days-picker">
            {DAYS_SHORT.map((d, i) => {
              const on = form.days_of_week?.includes(i);
              return (
                <button
                  key={d} type="button" onClick={() => toggleDay(i)}
                  data-testid={`show-day-toggle-${i}`}
                  className={`font-mono-ui text-xs uppercase tracking-[0.1em] px-2 py-2 border transition-colors ${on ? "bg-[var(--ff-red)] text-white border-[var(--ff-red)]" : "bg-[var(--ff-bg)] text-[var(--ff-text-dim)] border-[var(--ff-border)] hover:text-white"}`}
                >{d}</button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="px-3 py-2" data-testid="show-start-input" placeholder="Start" />
          <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="px-3 py-2" data-testid="show-end-input" placeholder="End" />
        </div>
        <p className="text-[0.65rem] text-[var(--ff-text-dim)] font-mono-ui">Leave days + times empty to save as an unscheduled draft.</p>

        {form.days_of_week && form.days_of_week.length > 0 && (
          <div className="space-y-2 border border-[var(--ff-border)] p-3" data-testid="show-day-times-section">
            <div className="label-eyebrow">Per-day times
              <span className="normal-case tracking-normal text-[var(--ff-text-dim)]"> (optional — blank uses default {form.start_time || "—"}–{form.end_time || "—"})</span>
            </div>
            {form.days_of_week.map((d) => {
              const ent = (form.day_times || {})[String(d)] || {};
              const custom = ent.start || ent.end;
              return (
                <div key={d} className="grid grid-cols-[54px_1fr_1fr_28px] gap-2 items-center">
                  <span className="font-mono-ui text-xs uppercase tracking-[0.1em] text-[var(--ff-text-dim)]">{DAYS_SHORT[d]}</span>
                  <input type="time" value={ent.start || ""} onChange={(e) => setDayTime(d, "start", e.target.value)} className="px-2 py-1.5 text-sm" data-testid={`show-daytime-start-${d}`} />
                  <input type="time" value={ent.end || ""} onChange={(e) => setDayTime(d, "end", e.target.value)} className="px-2 py-1.5 text-sm" data-testid={`show-daytime-end-${d}`} />
                  {custom
                    ? <button type="button" onClick={() => clearDayTime(d)} className="text-[var(--ff-text-dim)] hover:text-[var(--ff-red)] text-sm" data-testid={`show-daytime-clear-${d}`} title="Use default">✕</button>
                    : <span className="text-[0.55rem] text-[var(--ff-text-dim)] leading-tight">def</span>}
                </div>
              );
            })}
          </div>
        )}

        {form.days_of_week && form.days_of_week.length > 0 && (
          <div className="space-y-2 border border-[var(--ff-border)] p-3" data-testid="show-date-overrides-section">
            <div className="label-eyebrow">One-off date overrides
              <span className="normal-case tracking-normal text-[var(--ff-text-dim)]"> (a specific date airs at a different time)</span>
            </div>
            {Object.entries(form.date_overrides || {}).sort((a, b) => a[0].localeCompare(b[0])).map(([iso, v]) => (
              <div key={iso} className="grid grid-cols-[1fr_1fr_1fr_28px] gap-2 items-center">
                <span className="font-mono-ui text-[0.65rem] text-white">{iso}</span>
                <input type="time" value={v.start || ""} onChange={(e) => setDateOverride(iso, "start", e.target.value)} className="px-2 py-1.5 text-sm" data-testid={`show-dateovr-start-${iso}`} />
                <input type="time" value={v.end || ""} onChange={(e) => setDateOverride(iso, "end", e.target.value)} className="px-2 py-1.5 text-sm" data-testid={`show-dateovr-end-${iso}`} />
                <button type="button" onClick={() => removeDateOverride(iso)} className="text-[var(--ff-text-dim)] hover:text-[var(--ff-red)] text-sm" data-testid={`show-dateovr-remove-${iso}`} title="Remove">✕</button>
              </div>
            ))}
            <div className="flex gap-2 items-center">
              <input type="date" value={newOvrDate} onChange={(e) => setNewOvrDate(e.target.value)} className="px-2 py-1.5 text-sm flex-1" data-testid="show-dateovr-new-date" />
              <button type="button" onClick={addDateOverride} disabled={!newOvrDate} className="font-mono-ui text-xs uppercase tracking-[0.15em] px-3 py-1.5 border border-[var(--ff-border)] hover:border-[var(--ff-red)] hover:text-[var(--ff-red)] transition-colors disabled:opacity-40" data-testid="show-dateovr-add">+ Add date</button>
            </div>
          </div>
        )}
        <div className="space-y-2 border-y border-[var(--ff-border)] py-3 my-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.is_recurring} onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })} className="w-4 h-4 accent-[var(--ff-red)]" data-testid="show-recurring" />
            <span className="font-mono-ui text-xs uppercase tracking-[0.15em]">Recurring weekly episode</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.is_premiere} onChange={(e) => setForm({ ...form, is_premiere: e.target.checked })} className="w-4 h-4 accent-[var(--ff-red)]" data-testid="show-premiere" />
            <span className="font-mono-ui text-xs uppercase tracking-[0.15em]">Premiere ★</span>
          </label>
          {form.is_premiere && (
            <input type="date" value={form.premiere_date} onChange={(e) => setForm({ ...form, premiere_date: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="show-premiere-date" />
          )}
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.is_finale} onChange={(e) => setForm({ ...form, is_finale: e.target.checked })} className="w-4 h-4 accent-[var(--ff-red)]" data-testid="show-finale" />
            <span className="font-mono-ui text-xs uppercase tracking-[0.15em]">Season finale ⬢</span>
          </label>
          {form.is_finale && (
            <input type="date" value={form.finale_date} onChange={(e) => setForm({ ...form, finale_date: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="show-finale-date" />
          )}
          <CollabCheckbox checked={form.is_collab} onChange={(v) => setForm({ ...form, is_collab: v })} testId="show-collab" />
          <div className="space-y-2 pt-2 border-t border-[var(--ff-border)]">
            <div className="label-eyebrow">Season (optional)</div>
            <div className="grid grid-cols-3 gap-2">
              <input type="number" min="1" placeholder="Season #" value={form.season_number} onChange={(e) => setForm({ ...form, season_number: e.target.value })} className="px-3 py-2 text-sm" data-testid="show-season-number" />
              <input type="date" placeholder="Start" value={form.season_start} onChange={(e) => setForm({ ...form, season_start: e.target.value })} className="px-3 py-2 text-sm" data-testid="show-season-start" />
              <input type="date" placeholder="End" value={form.season_end} onChange={(e) => setForm({ ...form, season_end: e.target.value })} className="px-3 py-2 text-sm" data-testid="show-season-end" />
            </div>
            <p className="text-[0.65rem] text-[var(--ff-text-dim)] font-mono-ui">Set start → end dates to run the show as a limited season.</p>
          </div>
          <div className="space-y-2 pt-2 border-t border-[var(--ff-border)]">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.is_special} onChange={(e) => setForm({ ...form, is_special: e.target.checked })} className="w-4 h-4 accent-[var(--ff-red)]" data-testid="show-special" />
              <span className="font-mono-ui text-xs uppercase tracking-[0.15em]">Special Episode ✦</span>
            </label>
            {form.is_special && (
              <div className="space-y-2">
                <div className="flex gap-2 flex-wrap">
                  {[2, 4, 6, 12, 24].map((h) => (
                    <button key={h} type="button" onClick={() => setForm({ ...form, special_duration_hours: h })}
                      className={`font-mono-ui text-xs uppercase tracking-[0.1em] px-3 py-1.5 border transition-colors ${Number(form.special_duration_hours) === h ? "bg-[#AF52DE] text-white border-[#AF52DE]" : "border-[var(--ff-border)] text-[var(--ff-text-dim)] hover:text-white"}`}
                      data-testid={`show-special-preset-${h}`}>
                      {h}H
                    </button>
                  ))}
                </div>
                <input type="number" min="1" max="72" placeholder="Custom hours" value={form.special_duration_hours} onChange={(e) => setForm({ ...form, special_duration_hours: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="show-special-hours" />
                <div className="label-eyebrow pt-2">Frequency</div>
                <div className="grid grid-cols-3 gap-2" data-testid="show-special-frequency">
                  {[
                    ["one_day", "One-Day", "Just one airing"],
                    ["daily", "Daily", "Every day this week"],
                    ["weekly", "Weekly", "Once a week (uses days above)"],
                  ].map(([val, label, hint]) => (
                    <button key={val} type="button" onClick={() => setForm({ ...form, special_frequency: val })}
                      title={hint}
                      className={`font-mono-ui text-xs uppercase tracking-[0.1em] px-2 py-2 border transition-colors ${form.special_frequency === val ? "bg-[#AF52DE] text-white border-[#AF52DE]" : "border-[var(--ff-border)] text-[var(--ff-text-dim)] hover:text-white"}`}
                      data-testid={`show-special-freq-${val}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-[0.65rem] text-[var(--ff-text-dim)] font-mono-ui">Combine with Premiere + Finale dates below to bracket a limited special run.</p>
              </div>
            )}
          </div>
        </div>
        <input placeholder="YouTube URL" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} className="w-full px-3 py-2" data-testid="show-youtube-input" />
        <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2" rows={2} />
        <div>
          <label className="label-eyebrow block mb-2">Show Logo</label>
          <div className="flex items-start gap-3">
            {form.thumbnail_url ? (
              <img src={form.thumbnail_url} alt="show logo" className="w-16 h-16 object-cover border border-[var(--ff-border)]" data-testid="show-logo-preview" />
            ) : (
              <div className="w-16 h-16 bg-[var(--ff-bg)] border border-dashed border-[var(--ff-border)] flex items-center justify-center text-[var(--ff-text-dim)] text-xs">
                LOGO
              </div>
            )}
            <div className="flex-1 space-y-2">
              <label className="btn-ghost text-xs inline-flex cursor-pointer w-full justify-center">
                <Upload size={12} /> {uploadingLogo ? "Uploading..." : "Upload Logo"}
                <input type="file" accept="image/*" className="hidden" disabled={uploadingLogo} onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="show-logo-upload" />
              </label>
              <input placeholder="or paste image URL" value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} className="w-full px-3 py-2 text-xs" data-testid="show-logo-input" />
              {form.thumbnail_url && (
                <button type="button" onClick={() => setForm({ ...form, thumbnail_url: "" })} className="text-xs text-[var(--ff-red)] font-mono-ui uppercase tracking-[0.15em]" data-testid="show-logo-clear">Remove</button>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" min="1" max="12" placeholder="Month (optional)" value={form.month ?? ""} onChange={(e) => setForm({ ...form, month: e.target.value || null })} className="px-3 py-2" />
          <input type="number" placeholder="Year (optional)" value={form.year ?? ""} onChange={(e) => setForm({ ...form, year: e.target.value || null })} className="px-3 py-2" />
        </div>
        <p className="text-xs text-[var(--ff-text-dim)]">Month/year is optional for one-off month-specific airings.</p>
        <div className="flex gap-2">
          <button className="btn-primary flex-1 justify-center" data-testid={editingId ? "show-update-btn" : "show-create-btn"}>
            {editingId ? <Pencil size={14}/> : <Plus size={14}/>}
            {editingId ? "Update Show" : "Add Show"}
          </button>
          {editingId && <button type="button" onClick={reset} className="btn-ghost" data-testid="cancel-show-edit-btn"><X size={14}/>Cancel</button>}
        </div>
      </form>
      <div className="lg:col-span-2 space-y-4">
        {(() => {
          const unscheduled = shows.filter((s) => !s.start_time || !s.end_time || !(s.days_of_week && s.days_of_week.length > 0));
          if (unscheduled.length === 0) return null;
          return (
            <div className="surface-card p-4 border-l-4 border-l-[#FFD60A]" data-testid="unscheduled-shows">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <div className="label-eyebrow text-[#FFD60A]">▮ UNSCHEDULED · {unscheduled.length}</div>
                  <p className="text-xs text-[var(--ff-text-dim)] mt-1">Drafts saved without days/times. Click Schedule to assign a slot.</p>
                </div>
              </div>
              <div className="space-y-2">
                {unscheduled.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 p-3 bg-[var(--ff-bg)] border border-[var(--ff-border)]" data-testid={`unscheduled-row-${s.id}`}>
                    {s.thumbnail_url ? <img src={s.thumbnail_url} alt={s.title} className="w-10 h-10 object-cover" /> : <div className="w-10 h-10 bg-[var(--ff-surface)] border border-[var(--ff-border)] flex items-center justify-center text-[0.55rem] text-[var(--ff-text-dim)] font-mono-ui">DRAFT</div>}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold">{s.title}</div>
                      <div className="text-xs text-[var(--ff-text-dim)]">{s.category || "—"} · {(s.hosts || []).join(", ") || "no hosts"}</div>
                    </div>
                    <button onClick={() => startEdit(s)} className="btn-primary text-xs" data-testid={`schedule-btn-${s.id}`}>
                      <Plus size={12}/> Schedule
                    </button>
                    <button onClick={() => del(s.id)} className="text-[var(--ff-red)]"><Trash2 size={14}/></button>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        <div className="surface-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--ff-surface-hover)]">
            <tr><th className="text-left p-3 label-eyebrow">Logo</th><th className="text-left p-3 label-eyebrow">Title</th><th className="text-left p-3 label-eyebrow">Category</th><th className="text-left p-3 label-eyebrow">Hosts</th><th className="text-left p-3 label-eyebrow">Days / Time</th><th className="text-left p-3 label-eyebrow">Flags</th><th></th></tr>
          </thead>
          <tbody>
            {[
              ...shows.filter((s) => !s.is_collab),
              ...(shows.some((s) => s.is_collab) ? [{ _divider: true, id: "collab-divider" }] : []),
              ...shows.filter((s) => s.is_collab),
            ].map((s) => {
              if (s._divider) {
                return (
                  <tr key={s.id} className="border-t-2 border-[#5AC8FA]/40 bg-[#5AC8FA]/5" data-testid="shows-collab-divider">
                    <td colSpan={7} className="p-3">
                      <span className="label-eyebrow text-[#5AC8FA] inline-flex items-center gap-2">
                        <Handshake size={12} /> ▮ COLLABORATIONS · {shows.filter((x) => x.is_collab).length} — not FFPLLC property
                      </span>
                    </td>
                  </tr>
                );
              }
              const days = (Array.isArray(s.days_of_week) && s.days_of_week.length > 0) ? s.days_of_week : [s.day_of_week];
              return (
                <tr key={s.id} className={`border-t border-[var(--ff-border)] ${editingId === s.id ? "bg-[var(--ff-surface-hover)]" : ""}`} data-testid={`show-row-${s.id}`}>
                  <td className="p-3">
                    {s.thumbnail_url ? (
                      <img src={s.thumbnail_url} alt={s.title} className="w-10 h-10 object-cover" />
                    ) : (
                      <div className="w-10 h-10 bg-[var(--ff-bg)] border border-[var(--ff-border)] flex items-center justify-center text-[0.55rem] text-[var(--ff-text-dim)] font-mono-ui">NONE</div>
                    )}
                  </td>
                  <td className="p-3">{s.title}</td>
                  <td className="p-3 font-mono-ui text-xs text-[var(--ff-text-dim)]">{s.category || "—"}</td>
                  <td className="p-3 text-xs">{(s.hosts && s.hosts.length) ? s.hosts.join(", ") : <span className="text-[var(--ff-text-dim)]">—</span>}</td>
                  <td className="p-3 font-mono-ui text-xs" data-testid={`show-row-schedule-${s.id}`}>
                    {s.start_time && days.some((d) => d !== undefined && d !== null)
                      ? `${days.map((d) => DAYS_SHORT[d]).join(" · ")} / ${s.start_time}–${s.end_time}`
                      : <span className="text-[var(--ff-text-dim)]">Unscheduled / TBA</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {s.is_collab && <CollabBadge size="xs" testId={`show-list-collab-${s.id}`} />}
                      {s.is_premiere && <span className="font-mono-ui text-[0.6rem] uppercase tracking-[0.1em] px-1.5 py-0.5 border border-[#FFD60A] text-[#FFD60A]">★ PREM</span>}
                      {s.is_finale && <span className="font-mono-ui text-[0.6rem] uppercase tracking-[0.1em] px-1.5 py-0.5 border border-[var(--ff-red)] text-[var(--ff-red)]">⬢ FINALE</span>}
                      {s.is_special && <span className="font-mono-ui text-[0.6rem] uppercase tracking-[0.1em] px-1.5 py-0.5 border border-[#AF52DE] text-[#AF52DE]">✦ {s.special_duration_hours || "?"}H</span>}
                      {s.season_number && <span className="font-mono-ui text-[0.6rem] uppercase tracking-[0.1em] px-1.5 py-0.5 border border-[var(--ff-border)] text-[var(--ff-text-dim)]">S{s.season_number}</span>}
                      {s.is_recurring && <span className="font-mono-ui text-[0.6rem] uppercase tracking-[0.1em] px-1.5 py-0.5 border border-[var(--ff-border)] text-[var(--ff-text-dim)]">↻ WKLY</span>}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center gap-3 justify-end">
                      <button onClick={() => announce(s)} className="text-[var(--ff-text-dim)] hover:text-white" title="Announce on Discord" data-testid={`announce-show-${s.id}`}><Megaphone size={14}/></button>
                      <button onClick={() => startEdit(s)} className="text-[var(--ff-text-dim)] hover:text-white" data-testid={`edit-show-${s.id}`}><Pencil size={14}/></button>
                      <button onClick={() => del(s.id)} className="text-[var(--ff-red)]" data-testid={`del-show-${s.id}`}><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

function HostsAdmin() {
  const [hosts, setHosts] = useState([]);
  const emptyForm = { name: "", bio: "", avatar_url: "", title: "", social_url: "" };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = async () => { const { data } = await api.get("/hosts"); setHosts(data); };
  useEffect(() => { load(); }, []);
  const reset = () => { setForm(emptyForm); setEditingId(null); };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) { await api.put(`/hosts/${editingId}`, form); toast.success("Host updated"); }
      else { await api.post("/hosts", form); toast.success("Host added"); }
      reset(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEdit = (h) => { setEditingId(h.id); setForm({ name: h.name, bio: h.bio || "", avatar_url: h.avatar_url || "", title: h.title || "", social_url: h.social_url || "" }); };
  const del = async (id) => { if (!id) return; if (window.confirm("Delete host?")) { await api.delete(`/hosts/${id}`); if (editingId === id) reset(); load(); } };
  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${API_BASE.replace(/\/api$/, "")}${data.url}`;
      setForm((f) => ({ ...f, avatar_url: url }));
      toast.success("Avatar uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-8">
      <form onSubmit={submit} className="surface-card p-6 space-y-3" data-testid="host-form">
        <h3 className="font-display text-2xl mb-2">{editingId ? "Edit Host" : "Add Host"}</h3>
        <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2" data-testid="host-name-input" />
        <input placeholder="Title (e.g. Radio DJ, Producer)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2" data-testid="host-title-input" />
        <div>
          <label className="label-eyebrow block mb-2">Avatar</label>
          <div className="flex items-start gap-3">
            {form.avatar_url ? (
              <img src={form.avatar_url} alt="preview" className="w-16 h-16 object-cover border border-[var(--ff-border)]" data-testid="host-avatar-preview" />
            ) : (
              <div className="w-16 h-16 bg-[var(--ff-bg)] border border-dashed border-[var(--ff-border)] flex items-center justify-center text-[var(--ff-text-dim)] text-xs">
                {form.name ? form.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase() : "IMG"}
              </div>
            )}
            <div className="flex-1 space-y-2">
              <label className="btn-ghost text-xs inline-flex cursor-pointer w-full justify-center">
                <Upload size={12} />
                {uploading ? "Uploading..." : "Upload Image"}
                <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => uploadAvatar(e.target.files?.[0])} data-testid="host-avatar-upload" />
              </label>
              <input placeholder="or paste URL" value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} className="w-full px-3 py-2 text-xs" data-testid="host-avatar-input" />
              {form.avatar_url && (
                <button type="button" onClick={() => setForm({ ...form, avatar_url: "" })} className="text-xs text-[var(--ff-red)] font-mono-ui uppercase tracking-[0.15em]" data-testid="host-avatar-clear">Remove</button>
              )}
            </div>
          </div>
        </div>
        <input placeholder="Social / website URL" value={form.social_url} onChange={(e) => setForm({ ...form, social_url: e.target.value })} className="w-full px-3 py-2" />
        <textarea placeholder="Bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="w-full px-3 py-2" rows={5} data-testid="host-bio-input" />
        <p className="text-xs text-[var(--ff-text-dim)]">Hosts appearing on shows but without a bio record will still show on /hosts — add them here to give them a bio.</p>
        <div className="flex gap-2">
          <button className="btn-primary flex-1 justify-center" data-testid={editingId ? "host-update-btn" : "host-create-btn"}>
            {editingId ? <Pencil size={14}/> : <Plus size={14}/>}
            {editingId ? "Update Host" : "Add Host"}
          </button>
          {editingId && <button type="button" onClick={reset} className="btn-ghost" data-testid="cancel-host-edit-btn"><X size={14}/>Cancel</button>}
        </div>
      </form>
      <div className="lg:col-span-2 space-y-2">
        {hosts.length === 0 && <div className="surface-card p-6 text-[var(--ff-text-dim)] italic">No hosts on the roster yet.</div>}
        {hosts.map((h) => (
          <div key={h.name} className={`surface-card p-4 flex items-center gap-4 ${editingId === h.id ? "border-[var(--ff-red)]" : ""}`} data-testid={`host-admin-row-${h.name.replace(/\s+/g, "-").toLowerCase()}`}>
            {h.avatar_url ? (
              <img src={h.avatar_url} alt={h.name} className="w-14 h-14 object-cover" />
            ) : (
              <div className="w-14 h-14 bg-[var(--ff-red)] text-white flex items-center justify-center font-display text-lg">
                {h.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg">{h.name}</div>
              <div className="text-xs text-[var(--ff-text-dim)] font-mono-ui">
                {h.title || "—"} · {h.shows?.length || 0} SHOW{(h.shows?.length || 0) === 1 ? "" : "S"}
                {!h.id && <span className="ml-2 text-[var(--ff-yellow)]">NO BIO RECORD</span>}
              </div>
              {h.bio && <div className="text-xs text-[var(--ff-text-dim)] mt-1 line-clamp-1">{h.bio}</div>}
            </div>
            <div className="flex items-center gap-3">
              {h.id ? (
                <>
                  <button onClick={() => startEdit(h)} className="text-[var(--ff-text-dim)] hover:text-white" data-testid={`edit-host-${h.name.replace(/\s+/g, "-").toLowerCase()}`}><Pencil size={14}/></button>
                  <button onClick={() => del(h.id)} className="text-[var(--ff-red)]"><Trash2 size={14}/></button>
                </>
              ) : (
                <button onClick={() => startEdit({ ...h, id: null })} className="btn-ghost text-xs">Add Bio</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const CENSORABLE = [
  ["employee_id", "Emp ID"], ["employee_name", "Name"], ["investigator", "Investigator"],
  ["description", "Description"], ["findings", "Findings"],
];

function HRCases({ isAdmin }) {
  const [cases, setCases] = useState([]);
  const emptyForm = {
    case_number: "", employee_id: "", employee_name: "", date_opened: new Date().toISOString().slice(0, 10),
    status: "Open", investigator: "", description: "", findings: "", private_notes: "", censored_fields: [],
  };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const load = async () => { const { data } = await api.get("/hr-cases"); setCases(data); };
  useEffect(() => { load(); }, []);
  const reset = () => { setForm(emptyForm); setEditingId(null); };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) { await api.put(`/hr-cases/${editingId}`, form); toast.success("Case updated"); }
      else { await api.post("/hr-cases", form); toast.success("Case created"); }
      reset(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      case_number: c.case_number, employee_id: c.employee_id, employee_name: c.employee_name,
      date_opened: c.date_opened, status: c.status, investigator: c.investigator,
      description: c.description || "", findings: c.findings || "",
      private_notes: c.private_notes === "[REDACTED]" ? "" : (c.private_notes || ""),
      censored_fields: c.censored_fields || [],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const del = async (id) => { if (window.confirm("Delete case?")) { await api.delete(`/hr-cases/${id}`); if (editingId === id) reset(); load(); } };
  const toggleCensor = async (caseObj, field) => {
    const newFields = caseObj.censored_fields?.includes(field)
      ? caseObj.censored_fields.filter((f) => f !== field)
      : [...(caseObj.censored_fields || []), field];
    await api.put(`/hr-cases/${caseObj.id}`, { ...caseObj, censored_fields: newFields });
    load();
  };
  return (
    <div className="space-y-8">
      {isAdmin && (
        <form onSubmit={submit} className="surface-card p-6 grid md:grid-cols-3 gap-3" data-testid="hr-case-form">
          <h3 className="font-display text-2xl md:col-span-3 mb-2">Open New HR Case</h3>
          <input required placeholder="Case # (e.g., HR-2026-001)" value={form.case_number} onChange={(e) => setForm({ ...form, case_number: e.target.value })} className="px-3 py-2" data-testid="case-number-input" />
          <input required placeholder="Employee ID" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="px-3 py-2" data-testid="case-empid-input" />
          <input required placeholder="Employee Name" value={form.employee_name} onChange={(e) => setForm({ ...form, employee_name: e.target.value })} className="px-3 py-2" data-testid="case-name-input" />
          <input required type="date" value={form.date_opened} onChange={(e) => setForm({ ...form, date_opened: e.target.value })} className="px-3 py-2" />
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="px-3 py-2" data-testid="case-status-select">
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <input required placeholder="Investigator" value={form.investigator} onChange={(e) => setForm({ ...form, investigator: e.target.value })} className="px-3 py-2" />
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-3 px-3 py-2" rows={2} />
          <textarea placeholder="Findings" value={form.findings} onChange={(e) => setForm({ ...form, findings: e.target.value })} className="md:col-span-3 px-3 py-2" rows={2} />
          <textarea placeholder="Private notes (admin only)" value={form.private_notes} onChange={(e) => setForm({ ...form, private_notes: e.target.value })} className="md:col-span-3 px-3 py-2 border-l-4 border-l-[var(--ff-red)]" rows={2} data-testid="case-private-notes" />
          <div className="md:col-span-3">
            <div className="label-eyebrow mb-2">Censor for public:</div>
            <div className="flex flex-wrap gap-2">
              {CENSORABLE.map(([f, l]) => (
                <label key={f} className="flex items-center gap-2 bg-[var(--ff-bg)] px-3 py-1 border border-[var(--ff-border)] cursor-pointer text-xs font-mono-ui">
                  <input type="checkbox" checked={form.censored_fields.includes(f)} onChange={(e) => setForm({ ...form, censored_fields: e.target.checked ? [...form.censored_fields, f] : form.censored_fields.filter((x) => x !== f) })} />
                  {l}
                </label>
              ))}
            </div>
          </div>
          <button className="btn-primary md:col-span-3 justify-center" data-testid={editingId ? "update-case-btn" : "create-case-btn"}>
            {editingId ? <Pencil size={14}/> : <Plus size={14}/>}
            {editingId ? "Update Case" : "Create Case"}
          </button>
          {editingId && <button type="button" onClick={reset} className="btn-ghost md:col-span-3 justify-center" data-testid="cancel-case-edit-btn"><X size={14}/>Cancel Edit</button>}
        </form>
      )}
      <div className="space-y-2">
        {cases.map((c) => (
          <div key={c.id} className="surface-card p-4" data-testid={`case-card-${c.id}`}>
            <div className="grid md:grid-cols-6 gap-3 text-sm items-center">
              <div className="font-mono-ui">{c.case_number}</div>
              <div className="font-mono-ui text-xs">{c.employee_id}</div>
              <div>{c.employee_name}</div>
              <div className="text-[var(--ff-text-dim)]">{c.investigator}</div>
              <div className="font-mono-ui text-xs uppercase tracking-[0.15em] text-[var(--ff-red)]">{c.status}</div>
              <div className="text-right">
                {isAdmin && (
                  <div className="flex items-center gap-3 justify-end">
                    <button onClick={() => startEdit(c)} className="text-[var(--ff-text-dim)] hover:text-white" data-testid={`edit-case-${c.id}`}><Pencil size={14}/></button>
                    <button onClick={() => del(c.id)} className="text-[var(--ff-red)]"><Trash2 size={14}/></button>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 grid md:grid-cols-2 gap-3 text-xs text-[var(--ff-text-dim)]">
              <div><b className="text-white">Description:</b> {c.description}</div>
              <div><b className="text-white">Findings:</b> {c.findings || "—"}</div>
              {isAdmin && <div className="md:col-span-2 border-t border-[var(--ff-border)] pt-2"><b className="text-[var(--ff-red)]">Private Notes:</b> {c.private_notes || "—"}</div>}
            </div>
            {isAdmin && (
              <div className="mt-3 flex flex-wrap gap-2 items-center text-xs">
                <span className="label-eyebrow">Public censorship:</span>
                {CENSORABLE.map(([f, l]) => {
                  const on = c.censored_fields?.includes(f);
                  return (
                    <button key={f} onClick={() => toggleCensor(c, f)} className={`px-2 py-1 border font-mono-ui text-[0.65rem] uppercase ${on ? "bg-[var(--ff-red)] text-white border-[var(--ff-red)]" : "border-[var(--ff-border)] text-[var(--ff-text-dim)]"}`} data-testid={`censor-${c.id}-${f}`}>
                      {on ? <EyeOff size={10} className="inline mr-1" /> : <Eye size={10} className="inline mr-1" />} {l}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Applications() {
  const [apps, setApps] = useState([]);
  const load = async () => { const { data } = await api.get("/job-applications"); setApps(data); };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (window.confirm("Delete application?")) { await api.delete(`/job-applications/${id}`); load(); } };
  const downloadResume = async (fid) => {
    try {
      const res = await api.get(`/files/${fid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error("Failed to download"); }
  };
  return (
    <div className="surface-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[var(--ff-surface-hover)]">
          <tr><th className="text-left p-3 label-eyebrow">Date</th><th className="text-left p-3 label-eyebrow">Name</th><th className="text-left p-3 label-eyebrow">Position</th><th className="text-left p-3 label-eyebrow">Email</th><th className="text-left p-3 label-eyebrow">Phone</th><th className="text-left p-3 label-eyebrow">Resume</th><th></th></tr>
        </thead>
        <tbody>
          {apps.length === 0 && <tr><td colSpan={7} className="p-6 text-[var(--ff-text-dim)] italic">No applications yet.</td></tr>}
          {apps.map((a) => (
            <tr key={a.id} className="border-t border-[var(--ff-border)]" data-testid={`app-row-${a.id}`}>
              <td className="p-3 font-mono-ui text-xs text-[var(--ff-text-dim)]">{a.created_at?.slice(0, 10)}</td>
              <td className="p-3">{a.name}</td>
              <td className="p-3">{a.position}</td>
              <td className="p-3 font-mono-ui text-xs">{a.email}</td>
              <td className="p-3 font-mono-ui text-xs">{a.phone}</td>
              <td className="p-3">{a.resume_file_id ? <button onClick={() => downloadResume(a.resume_file_id)} className="text-[var(--ff-red)]" data-testid={`download-resume-${a.id}`}><Download size={14}/></button> : "—"}</td>
              <td className="p-3 text-right"><button onClick={() => del(a.id)} className="text-[var(--ff-red)]"><Trash2 size={14}/></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function AboutEditor() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(-1);

  const load = async () => {
    const { data } = await api.get("/site-content/about");
    setC({
      eyebrow: data.eyebrow || "",
      headline: data.headline || "",
      intro: data.intro || "",
      body: data.body || "",
      stats: data.stats || [],
      gallery: data.gallery || [],
    });
  };
  useEffect(() => { load(); }, []);

  if (!c) return <div className="text-[var(--ff-text-dim)]">Loading...</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site-content/about", c);
      toast.success("About page saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const addStat = () => setC({ ...c, stats: [...c.stats, { label: "", value: "" }] });
  const updStat = (i, k, v) => { const s = [...c.stats]; s[i] = { ...s[i], [k]: v }; setC({ ...c, stats: s }); };
  const rmStat = (i) => setC({ ...c, stats: c.stats.filter((_, x) => x !== i) });

  const addGal = () => setC({ ...c, gallery: [...c.gallery, { title: "", image_url: "" }] });
  const updGal = (i, k, v) => { const g = [...c.gallery]; g[i] = { ...g[i], [k]: v }; setC({ ...c, gallery: g }); };
  const rmGal = (i) => setC({ ...c, gallery: c.gallery.filter((_, x) => x !== i) });

  const uploadGalImage = async (idx, file) => {
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updGal(idx, "image_url", `${API_BASE.replace(/\/api$/, "")}${data.url}`);
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploadingIdx(-1); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-8" data-testid="about-editor">
      <div className="lg:col-span-2 space-y-4">
        <div className="surface-card p-6 space-y-3">
          <h3 className="font-display text-2xl mb-2">Hero</h3>
          <label className="label-eyebrow block">Eyebrow</label>
          <input value={c.eyebrow} onChange={(e) => setC({ ...c, eyebrow: e.target.value })} className="w-full px-3 py-2" data-testid="about-eyebrow-input" />
          <label className="label-eyebrow block">Headline</label>
          <textarea value={c.headline} onChange={(e) => setC({ ...c, headline: e.target.value })} rows={2} className="w-full px-3 py-2" data-testid="about-headline-input" />
          <label className="label-eyebrow block">Intro paragraph (highlighted)</label>
          <textarea value={c.intro} onChange={(e) => setC({ ...c, intro: e.target.value })} rows={3} className="w-full px-3 py-2" data-testid="about-intro-input" />
          <label className="label-eyebrow block">Body (separate paragraphs with a blank line)</label>
          <textarea value={c.body} onChange={(e) => setC({ ...c, body: e.target.value })} rows={10} className="w-full px-3 py-2 font-mono-ui text-sm" data-testid="about-body-input" />
        </div>

        <div className="surface-card p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-display text-2xl">Gallery</h3>
            <button onClick={addGal} className="btn-ghost text-xs" data-testid="add-gallery-btn"><Plus size={12}/>Add Image</button>
          </div>
          <div className="space-y-3">
            {c.gallery.map((g, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-[var(--ff-bg)] border border-[var(--ff-border)]" data-testid={`gallery-item-${i}`}>
                {g.image_url ? (
                  <img src={g.image_url} alt={g.title} className="w-20 h-20 object-cover" />
                ) : (
                  <div className="w-20 h-20 bg-[var(--ff-surface)] border border-dashed border-[var(--ff-border)] flex items-center justify-center text-[0.6rem] text-[var(--ff-text-dim)]">NO IMAGE</div>
                )}
                <div className="flex-1 space-y-2">
                  <input placeholder="Caption (e.g. TEAM)" value={g.title} onChange={(e) => updGal(i, "title", e.target.value)} className="w-full px-2 py-1 text-sm" data-testid={`gallery-title-${i}`} />
                  <div className="flex gap-2">
                    <label className="btn-ghost text-xs cursor-pointer flex-1 justify-center">
                      <Upload size={10} /> {uploadingIdx === i ? "..." : "Upload"}
                      <input type="file" accept="image/*" className="hidden" disabled={uploadingIdx === i} onChange={(e) => uploadGalImage(i, e.target.files?.[0])} data-testid={`gallery-upload-${i}`} />
                    </label>
                    <input placeholder="or URL" value={g.image_url} onChange={(e) => updGal(i, "image_url", e.target.value)} className="flex-1 px-2 py-1 text-xs" />
                  </div>
                </div>
                <button onClick={() => rmGal(i)} className="text-[var(--ff-red)]" data-testid={`remove-gallery-${i}`}><Trash2 size={14}/></button>
              </div>
            ))}
            {c.gallery.length === 0 && <div className="text-[var(--ff-text-dim)] italic text-sm">No gallery images yet.</div>}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="surface-card p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-display text-2xl">Stats Sidebar</h3>
            <button onClick={addStat} className="btn-ghost text-xs" data-testid="add-stat-btn"><Plus size={12}/>Add</button>
          </div>
          <div className="space-y-2">
            {c.stats.map((s, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`stat-item-${i}`}>
                <input placeholder="Label" value={s.label} onChange={(e) => updStat(i, "label", e.target.value)} className="flex-1 px-2 py-1 text-xs" data-testid={`stat-label-${i}`} />
                <input placeholder="Value" value={s.value} onChange={(e) => updStat(i, "value", e.target.value)} className="flex-1 px-2 py-1 text-xs" data-testid={`stat-value-${i}`} />
                <button onClick={() => rmStat(i)} className="text-[var(--ff-red)]"><Trash2 size={14}/></button>
              </div>
            ))}
            {c.stats.length === 0 && <div className="text-[var(--ff-text-dim)] italic text-xs">No stats yet.</div>}
          </div>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center" data-testid="save-about-btn">
          {saving ? "Saving..." : "Save About Page"}
        </button>
        <a href="/about" target="_blank" rel="noreferrer" className="btn-ghost w-full text-center justify-center flex" data-testid="preview-about-link">
          Preview /about →
        </a>
      </div>
    </div>
  );
}
